// src/pages/EditRide.js
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Autocomplete } from "@react-google-maps/api";

const API_WRK = process.env.REACT_APP_API_WRK;
const API_BASE = API_WRK?.replace("/api/worker", "");

const EditRide = () => {
  const { rideId } = useParams();
  const navigate = useNavigate();

  const pickupRef = useRef(null);
  const pickupAutocompleteRef = useRef(null);

  const stopCityRef = useRef(null);
  const stopCityAutocompleteRef = useRef(null);

  const stopPickupRef = useRef(null);
  const stopPickupAutocompleteRef = useRef(null);

  const [formData, setFormData] = useState({
    date: "",
    time: "",
    from: "",
    to: "",
    pickupLocation: "",
    pricePerSeat: "",
    seatsAvailable: "",
    stops: [],
    currentStop: { name: "", pick: "", price: "", time: "" },
  });

  const [loading, setLoading] = useState(true);

  // ----------------------------------
  // LOAD RIDE DATA
  // ----------------------------------
  useEffect(() => {
    const fetchRide = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/ride/${rideId}`);
        const r = res.data;

        setFormData({
          date: r.date,
          time: r.time,
          from: r.from,
          to: r.to,
          pickupLocation: r.pickupLocation,
          pricePerSeat: r.pricePerSeat,
          seatsAvailable: r.seatsAvailable,
          stops: r.stops || [],
          currentStop: { name: "", price: "", time: "" },
        });

        if (pickupRef.current) pickupRef.current.value = r.pickupLocation;
      } catch (err) {
        console.error("❌ Failed to fetch ride:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRide();
  }, [rideId]);

  // ----------------------------------
  // FORM HANDLERS
  // ----------------------------------
  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleStopChange = (e) => {
    setFormData((p) => ({
      ...p,
      currentStop: { ...p.currentStop, [e.target.name]: e.target.value },
    }));
  };

  // AUTOCOMPLETE HANDLERS
  const handlePickupPlaceChanged = () => {
    const place = pickupAutocompleteRef.current.getPlace();
    if (place?.formatted_address) {
      pickupRef.current.value = place.formatted_address;
    }
  };

  const handleStopCityChanged = () => {
    const place = stopCityAutocompleteRef.current.getPlace();
    if (place?.formatted_address) {
      setFormData((p) => ({
        ...p,
        currentStop: { ...p.currentStop, name: place.formatted_address },
      }));
    }
  };

  const handleStopPickupChanged = () => {
    const place = stopPickupAutocompleteRef.current.getPlace();
    if (place?.formatted_address) {
      stopPickupRef.current.value = place.formatted_address;
    }
  };

  // ADD STOP
  const handleAddStop = () => {
    const { name, price, time } = formData.currentStop;
    const pickup = stopPickupRef.current?.value || "";

    if (name && pickup && price && time) {
      setFormData((p) => ({
        ...p,
        stops: [...p.stops, { name, pickup, price, time }],
        currentStop: { name: "", price: "", time: "" },
      }));

      stopCityRef.current.value = "";
      stopPickupRef.current.value = "";
    }
  };

  // REMOVE STOP
  const handleRemoveStop = (i) => {
    setFormData((p) => ({
      ...p,
      stops: p.stops.filter((_, idx) => idx !== i),
    }));
  };

  // ----------------------------------
  // SUBMIT UPDATE
  // ----------------------------------
  const handleSubmit = async () => {
    try {
      const updatedRide = {
        ...formData,
        pickupLocation: pickupRef.current?.value || formData.pickupLocation,
        pricePerSeat: parseFloat(formData.pricePerSeat),
        seatsAvailable: parseInt(formData.seatsAvailable),
      };

      await axios.put(`${API_BASE}/api/ride/update/${rideId}`, updatedRide);
      navigate("/ride/my-rides");
    } catch (err) {
      console.error("❌ Failed to update:", err);
      alert("Error saving ride");
    }
  };

  if (loading) return <div className="text-center mt-10">Loading...</div>;

  // ----------------------------------
  // SUPER SEXY UI STARTS HERE 🔥
  // ----------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-100 py-10 px-4 font-[Poppins]">
      <div className="max-w-2xl mx-auto space-y-12">

        {/* HEADER CARD */}
        <div className="bg-white rounded-3xl shadow-xl p-6 border border-indigo-100">
          <h2 className="text-3xl font-extrabold text-gray-800">
            Edit Ride
          </h2>
          <p className="text-gray-600 mt-1">
            {formData.from} → {formData.to}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            *Ride date cannot be changed after publishing.
          </p>
        </div>

        {/* BASIC INFO */}
        <div className="bg-white rounded-3xl shadow-lg p-6 border border-gray-100 space-y-4">
          <h3 className="font-bold text-lg text-gray-800">Basic Details</h3>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-700">Date</label>
              <input
                disabled
                value={formData.date}
                className="w-full mt-1 border rounded-lg p-2 bg-gray-100 text-gray-500"
              />
            </div>

            <div>
              <label className="text-sm text-gray-700">Time</label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                className="w-full mt-1 border rounded-lg p-2"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-700">Pickup Location</label>
            <Autocomplete
              onLoad={(ac) => (pickupAutocompleteRef.current = ac)}
              onPlaceChanged={handlePickupPlaceChanged}
            >
              <input
                ref={pickupRef}
                defaultValue={formData.pickupLocation}
                className="w-full mt-1 border rounded-lg p-2"
              />
            </Autocomplete>
          </div>
        </div>

        {/* PRICE & SEATS */}
        <div className="bg-white rounded-3xl shadow-lg p-6 border border-gray-100 space-y-4">
          <h3 className="font-bold text-lg text-gray-800">Pricing & Seats</h3>

          <input
            name="seatsAvailable"
            type="number"
            value={formData.seatsAvailable}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="Seats Available"
          />

          <input
            name="pricePerSeat"
            type="number"
            value={formData.pricePerSeat}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="Price Per Seat"
          />
        </div>

        {/* STOPS SECTION */}
        <div className="bg-white rounded-3xl shadow-lg p-6 border border-gray-100 space-y-4">
          <h3 className="font-bold text-lg text-gray-800">Optional Stops</h3>
          <p className="text-xs text-gray-500">
            Add more pickup points; passengers will see these during booking.
          </p>

          <Autocomplete
            onLoad={(a) => (stopCityAutocompleteRef.current = a)}
            onPlaceChanged={handleStopCityChanged}
          >
            <input
              ref={stopCityRef}
              placeholder="Stop City"
              className="w-full border rounded-lg p-2"
            />
          </Autocomplete>

          <Autocomplete
            onLoad={(a) => (stopPickupAutocompleteRef.current = a)}
            onPlaceChanged={handleStopPickupChanged}
          >
            <input
              ref={stopPickupRef}
              placeholder="Pickup Location"
              className="w-full border rounded-lg p-2"
            />
          </Autocomplete>

          <div className="grid grid-cols-2 gap-3">
            <input
              name="price"
              placeholder="Stop Price"
              value={formData.currentStop.price}
              onChange={handleStopChange}
              className="border rounded-lg p-2"
            />

            <input
              name="time"
              type="time"
              value={formData.currentStop.time}
              onChange={handleStopChange}
              className="border rounded-lg p-2"
            />
          </div>

          <button
            onClick={handleAddStop}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-xl font-semibold"
          >
            ➕ Add Stop
          </button>

          {formData.stops.length > 0 && (
            <div className="space-y-2 mt-4">
              {formData.stops.map((s, i) => (
                <div
                  key={i}
                  className="bg-indigo-50 p-3 rounded-xl flex justify-between items-center"
                >
                  <div>
                    <p className="font-semibold text-gray-800">{s.name}</p>
                    <p className="text-xs text-gray-600">{s.pickup}</p>
                    <p className="text-xs text-gray-700 mt-1">
                      {s.time} • ${s.price}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveStop(i)}
                    className="text-red-500 text-sm"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SAVE */}
        <div className="bg-white rounded-3xl shadow p-6">
          <button
            onClick={handleSubmit}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-bold text-lg"
          >
            ✅ Save Changes
          </button>
        </div>

      </div>
    </div>
  );
};

export default EditRide;
